import { supabase } from '../../shared/supabase/supabaseClient';

// ── Tipos ──

export type ServiceRecordItem = {
  id: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: 'text' | 'textarea' | 'number' | 'select' | 'multiselect' | 'date' | 'photo';
  valueText: string | null;
  valueNumber: number | null;
  valueDate: string | null;
  valueJson: unknown | null;
  sortOrder: number;
};

export type ServiceRecord = {
  id: string;
  appointmentId: string;
  customerId: string;
  branchId: string;
  performedByUserId: string | null;
  serviceProductId: string | null;
  visitDate: string;
  observations: string | null;
  recommendedNextService: string | null;
  recommendedReturnDate: string | null;
  items: ServiceRecordItem[];
  createdAt: string;
  updatedAt: string;
};

export type CustomerNote = {
  id: string;
  customerId: string;
  authorUserId: string | null;
  body: string;
  visibility: 'team' | 'restricted';
  isAlert: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CustomFieldDef = {
  id: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: 'text' | 'textarea' | 'number' | 'select' | 'multiselect' | 'date' | 'photo';
  options: unknown | null;
  required: boolean;
  visibility: 'team' | 'restricted';
  sortOrder: number;
};

export type CustomFieldValue = {
  id: string;
  customFieldId: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: CustomFieldDef['fieldType'];
  valueText: string | null;
  valueNumber: number | null;
  valueDate: string | null;
  valueJson: unknown | null;
  updatedAt: string;
};

// ── Service records ──

export async function listCustomerServiceRecords(customerId: string): Promise<ServiceRecord[]> {
  const { data, error } = await supabase.rpc('list_customer_service_records', {
    p_customer_id: customerId,
  });
  if (error || !Array.isArray(data)) return [];
  return data.map(mapServiceRecord);
}

function mapServiceRecord(row: Record<string, unknown>): ServiceRecord {
  return {
    id: String(row.id),
    appointmentId: String(row.appointment_id),
    customerId: String(row.customer_id),
    branchId: String(row.branch_id),
    performedByUserId: row.performed_by_user_id ? String(row.performed_by_user_id) : null,
    serviceProductId: row.service_product_id ? String(row.service_product_id) : null,
    visitDate: String(row.visit_date),
    observations: typeof row.observations === 'string' ? row.observations : null,
    recommendedNextService: typeof row.recommended_next_service === 'string' ? row.recommended_next_service : null,
    recommendedReturnDate: typeof row.recommended_return_date === 'string' ? row.recommended_return_date : null,
    items: Array.isArray(row.items) ? (row.items as Record<string, unknown>[]).map(mapServiceRecordItem) : [],
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at),
  };
}

function mapServiceRecordItem(row: Record<string, unknown>): ServiceRecordItem {
  return {
    id: String(row.id),
    fieldKey: String(row.fieldKey),
    fieldLabel: String(row.fieldLabel),
    fieldType: row.fieldType as ServiceRecordItem['fieldType'],
    valueText: typeof row.valueText === 'string' ? row.valueText : null,
    valueNumber: row.valueNumber != null ? Number(row.valueNumber) : null,
    valueDate: typeof row.valueDate === 'string' ? row.valueDate : null,
    valueJson: row.valueJson ?? null,
    sortOrder: Number(row.sortOrder ?? 0),
  };
}

export type UpsertServiceRecordPayload = {
  appointmentId?: string;
  recordId?: string;
  customerId?: string;
  branchId?: string;
  performedByUserId?: string;
  serviceProductId?: string;
  visitDate?: string;
  observations?: string;
  recommendedNextService?: string;
  recommendedReturnDate?: string;
  items?: Array<{
    fieldKey: string;
    fieldLabel: string;
    fieldType?: string;
    valueText?: string;
    valueNumber?: number;
    valueDate?: string;
    valueJson?: unknown;
    sortOrder?: number;
  }>;
};

export async function upsertAppointmentServiceRecord(
  payload: UpsertServiceRecordPayload,
): Promise<string> {
  const { data, error } = await supabase.rpc('upsert_appointment_service_record', {
    p_payload: payload as unknown as Record<string, unknown>,
  });
  if (error) throw error;
  const result = data as Record<string, unknown> | null;
  const recordId = result?.recordId ? String(result.recordId) : null;
  if (!recordId) throw new Error('customer_service_record_upsert_failed');
  return recordId;
}

// ── Customer notes ──

export async function listCustomerNotes(customerId: string, limit = 50, offset = 0): Promise<CustomerNote[]> {
  const { data, error } = await supabase.rpc('list_customer_notes', {
    p_customer_id: customerId,
    p_limit: limit,
    p_offset: offset,
  });
  if (error || !Array.isArray(data)) return [];
  return data.map(mapCustomerNote);
}

function mapCustomerNote(row: Record<string, unknown>): CustomerNote {
  return {
    id: String(row.id),
    customerId: String(row.customer_id),
    authorUserId: row.author_user_id ? String(row.author_user_id) : null,
    body: String(row.body),
    visibility: row.visibility as CustomerNote['visibility'],
    isAlert: Boolean(row.is_alert),
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at),
  };
}

export type UpsertNotePayload = {
  noteId?: string;
  customerId: string;
  body: string;
  visibility?: 'team' | 'restricted';
  isAlert?: boolean;
};

export async function upsertCustomerNote(payload: UpsertNotePayload): Promise<string> {
  const { data, error } = await supabase.rpc('upsert_customer_note', {
    p_payload: payload as unknown as Record<string, unknown>,
  });
  if (error) throw error;
  const result = data as Record<string, unknown> | null;
  const noteId = result?.noteId ? String(result.noteId) : null;
  if (!noteId) throw new Error('customer_note_upsert_failed');
  return noteId;
}

export async function archiveCustomerNote(noteId: string): Promise<void> {
  const { data, error } = await supabase.rpc('archive_customer_note', {
    p_note_id: noteId,
  });
  if (error) throw error;
  if (!data || !(data as Record<string, unknown>).ok) {
    throw new Error('customer_note_archive_failed');
  }
}

// ── Custom fields ──

export async function getCustomerCustomFields(
  businessTypeCode?: string | null,
): Promise<CustomFieldDef[]> {
  const { data, error } = await supabase.rpc('get_customer_custom_fields', {
    p_business_type_code: businessTypeCode ?? null,
  });
  if (error || !Array.isArray(data)) return [];
  return data.map(mapCustomFieldDef);
}

function mapCustomFieldDef(row: Record<string, unknown>): CustomFieldDef {
  return {
    id: String(row.id),
    fieldKey: String(row.field_key),
    fieldLabel: String(row.field_label),
    fieldType: row.field_type as CustomFieldDef['fieldType'],
    options: row.options ?? null,
    required: Boolean(row.required),
    visibility: row.visibility as CustomFieldDef['visibility'],
    sortOrder: Number(row.sort_order ?? 0),
  };
}

export async function listCustomerCustomFieldValues(
  customerId: string,
): Promise<CustomFieldValue[]> {
  const { data, error } = await supabase.rpc('list_customer_custom_field_values', {
    p_customer_id: customerId,
  });
  if (error || !Array.isArray(data)) return [];
  return data.map(mapCustomFieldValue);
}

function mapCustomFieldValue(row: Record<string, unknown>): CustomFieldValue {
  return {
    id: String(row.id),
    customFieldId: String(row.custom_field_id),
    fieldKey: String(row.field_key),
    fieldLabel: String(row.field_label),
    fieldType: row.field_type as CustomFieldValue['fieldType'],
    valueText: typeof row.value_text === 'string' ? row.value_text : null,
    valueNumber: row.value_number != null ? Number(row.value_number) : null,
    valueDate: typeof row.value_date === 'string' ? row.value_date : null,
    valueJson: row.value_json ?? null,
    updatedAt: String(row.updated_at),
  };
}

export type UpsertCustomFieldValuePayload = {
  customerId: string;
  customFieldId: string;
  valueText?: string;
  valueNumber?: number;
  valueDate?: string;
  valueJson?: unknown;
};

export async function upsertCustomerCustomFieldValue(
  payload: UpsertCustomFieldValuePayload,
): Promise<void> {
  const { data, error } = await supabase.rpc('upsert_customer_custom_field_value', {
    p_payload: payload as unknown as Record<string, unknown>,
  });
  if (error) throw error;
  if (!data || !(data as Record<string, unknown>).ok) {
    throw new Error('customer_custom_field_upsert_failed');
  }
}

// ── Historial de ventas ──

export type CustomerSaleRow = {
  id: string;
  createdAt: string;
  total: number;
  status: string;
  paymentStatus: string;
  fiscalStatus: string | null;
  branchId: string;
  shortId: string;
};

export async function listCustomerSales(
  customerId: string,
  limit = 20,
  offset = 0,
): Promise<CustomerSaleRow[]> {
  const { data, error } = await supabase.rpc('list_customer_sales', {
    p_customer_id: customerId,
    p_limit: limit,
    p_offset: offset,
  });
  if (error || !Array.isArray(data)) return [];
  return data.map((row: Record<string, unknown>) => ({
    id: String(row.id),
    createdAt: String(row.created_at),
    total: Number(row.total ?? 0),
    status: String(row.status ?? 'active'),
    paymentStatus: String(row.payment_status ?? 'pending'),
    fiscalStatus: (row.fiscal_status as string) ?? null,
    branchId: String(row.branch_id ?? ''),
    shortId: String(row.short_id ?? ''),
  }));
}

// ── Customer tasks ──

export type CustomerTask = {
  id: string;
  customerId: string;
  authorUserId: string | null;
  title: string;
  kind: 'call' | 'message' | 'visit' | 'task';
  dueDate: string | null;
  status: 'pending' | 'done';
  completedAt: string | null;
  completedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function listCustomerTasks(
  customerId: string,
  limit = 50,
  offset = 0,
): Promise<CustomerTask[]> {
  const { data, error } = await supabase.rpc('list_customer_tasks', {
    p_customer_id: customerId,
    p_limit: limit,
    p_offset: offset,
  });
  if (error || !Array.isArray(data)) return [];
  return data.map(mapCustomerTask);
}

function mapCustomerTask(row: Record<string, unknown>): CustomerTask {
  return {
    id: String(row.id),
    customerId: String(row.customer_id),
    authorUserId: row.author_user_id ? String(row.author_user_id) : null,
    title: String(row.title),
    kind: row.kind as CustomerTask['kind'],
    dueDate: row.due_date ? String(row.due_date) : null,
    status: row.status as CustomerTask['status'],
    completedAt: row.completed_at ? String(row.completed_at) : null,
    completedByUserId: row.completed_by_user_id ? String(row.completed_by_user_id) : null,
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at),
  };
}

export type UpsertTaskPayload = {
  taskId?: string;
  customerId: string;
  title: string;
  kind?: 'call' | 'message' | 'visit' | 'task';
  dueDate?: string | null;
};

export async function upsertCustomerTask(payload: UpsertTaskPayload): Promise<string> {
  const { data, error } = await supabase.rpc('upsert_customer_task', {
    p_payload: payload as unknown as Record<string, unknown>,
  });
  if (error) throw error;
  const result = data as Record<string, unknown> | null;
  const taskId = result?.taskId ? String(result.taskId) : null;
  if (!taskId) throw new Error('customer_task_upsert_failed');
  return taskId;
}

export async function completeCustomerTask(taskId: string): Promise<void> {
  const { data, error } = await supabase.rpc('complete_customer_task', {
    p_task_id: taskId,
  });
  if (error) throw error;
  if (!data || !(data as Record<string, unknown>).ok) {
    throw new Error('customer_task_complete_failed');
  }
}

export async function archiveCustomerTask(taskId: string): Promise<void> {
  const { data, error } = await supabase.rpc('archive_customer_task', {
    p_task_id: taskId,
  });
  if (error) throw error;
  if (!data || !(data as Record<string, unknown>).ok) {
    throw new Error('customer_task_archive_failed');
  }
}

// ── Bandeja global (F-19) ──

export type PendingTaskRow = {
  id: string;
  customerId: string;
  customerName: string;
  title: string;
  kind: 'call' | 'message' | 'visit' | 'task';
  dueDate: string | null;
  status: 'pending';
  createdAt: string;
};

export async function listPendingTasks(
  limit = 50,
  offset = 0,
): Promise<PendingTaskRow[]> {
  const { data, error } = await supabase.rpc('list_pending_tasks', {
    p_limit: limit,
    p_offset: offset,
  });
  if (error || !Array.isArray(data)) return [];
  return data.map((row: Record<string, unknown>) => ({
    id: String(row.id),
    customerId: String(row.customer_id),
    customerName: String(row.customer_name ?? ''),
    title: String(row.title),
    kind: row.kind as PendingTaskRow['kind'],
    dueDate: row.due_date ? String(row.due_date) : null,
    status: 'pending' as const,
    createdAt: String(row.created_at),
  }));
}

export async function countPendingTasks(): Promise<number> {
  const { data, error } = await supabase.rpc('count_pending_tasks');
  if (error || data == null) return 0;
  return Number(data);
}
